// hooks/useGlobalSearch.js - Server-side global search for header
import { useState, useCallback, useRef } from 'react';

const SEARCH_LIMIT = 50;

const buildOrClause = (field, wordStart, spaceWord) => {
  switch (field) {
    case 'title':
      return `title.ilike.${wordStart},title.ilike.${spaceWord},alternate_title.ilike.${wordStart},alternate_title.ilike.${spaceWord}`;
    case 'director':
      return `director.ilike.${wordStart},director.ilike.${spaceWord}`;
    case 'actor':
      return `actors.ilike.${wordStart},actors.ilike.${spaceWord}`;
    case 'year':
      return `year.ilike.${wordStart}`;
    case 'genre':
      return `genre.ilike.${wordStart},genre.ilike.${spaceWord}`;
    default:
      return `title.ilike.${wordStart},title.ilike.${spaceWord},alternate_title.ilike.${wordStart},alternate_title.ilike.${spaceWord},director.ilike.${wordStart},director.ilike.${spaceWord},actors.ilike.${wordStart},actors.ilike.${spaceWord},genre.ilike.${wordStart},genre.ilike.${spaceWord},studio.ilike.${wordStart},studio.ilike.${spaceWord},distributor.ilike.${wordStart},distributor.ilike.${spaceWord},year.ilike.${wordStart},language.ilike.${wordStart}`;
  }
};

export const useGlobalSearch = supabase => {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef(null);

  const performSearch = useCallback(
    async (query, field = 'all') => {
      if (!supabase || !query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);

      try {
        const trimmedQuery = query.trim();
        const wordStart = `${trimmedQuery}%`;
        const spaceWord = `% ${trimmedQuery}%`;

        // Search media_titles and movie_releases in parallel
        const [{ data, error }, { data: releaseMatches, error: releaseError }] = await Promise.all([
          supabase
            .from('media_titles')
            .select(
              `id, display_id, title, alternate_title, director, year, genre, actors, studio, distributor, cover_image_url`
            )
            .or(buildOrClause(field, wordStart, spaceWord))
            .order('created_at', { ascending: false })
            .limit(SEARCH_LIMIT),
          supabase
            .from('movie_releases')
            .select('master_movie_id')
            .or(`release_title.ilike.${wordStart},release_title.ilike.${spaceWord}`)
            .limit(SEARCH_LIMIT),
        ]);

        if (error) {
          console.error('Global search error:', error);
          setSearchResults([]);
          return;
        }

        if (releaseError) {
          console.error('Release title search error:', releaseError);
        }

        // Get master movie IDs from release matches not already in results
        const existingIds = new Set((data || []).map(m => m.id));
        const extraIds = [...new Set((releaseMatches || []).map(r => r.master_movie_id))].filter(
          id => !existingIds.has(id)
        );

        let combined = data || [];

        if (extraIds.length > 0) {
          const { data: extraMovies } = await supabase
            .from('media_titles')
            .select(
              `id, display_id, title, alternate_title, director, year, genre, actors, studio, distributor, cover_image_url`
            )
            .in('id', extraIds);
          combined = [...combined, ...(extraMovies || [])];
        }

        setSearchResults(combined);
      } catch (err) {
        console.error('Global search error:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [supabase]
  );

  const debouncedSearch = useCallback(
    (query, field = 'all') => {
      setSearchQuery(query);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!query.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      debounceRef.current = setTimeout(() => {
        performSearch(query, field);
      }, 300);
    },
    [performSearch]
  );

  const clearSearch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  }, []);

  return {
    searchResults,
    isSearching,
    searchQuery,
    search: debouncedSearch,
    clearSearch,
    hasResults: searchResults.length > 0,
  };
};

export default useGlobalSearch;
